import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeftRight, Calculator } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { expressCategories, unitsByCategory } from '../data/mock';
import { convertUnit, formatNumber } from '../utils/conversion';

// Map category IDs to their translation key prefixes
const getUnitTranslationKey = (categoryId) => {
  const keyMap = {
    'length': 'lengthUnits',
    'area': 'areaUnits',
    'weight': 'weightUnits',
    'volume': 'volumeUnits',
    'temperature': 'temperatureUnits',
    'speed': 'speedUnits',
    'time': 'timeUnits'
  };
  return keyMap[categoryId] || `${categoryId}Units`;
};

const ExpressConverter = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('length');
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [fromValue, setFromValue] = useState('1');
  const [toValue, setToValue] = useState('');

  const currentUnits = useMemo(() => unitsByCategory[activeTab] || [], [activeTab]);

  useEffect(() => {
    // Reset units when category changes
    if (currentUnits.length >= 2) {
      setFromUnit(currentUnits[0].id);
      setToUnit(currentUnits[1].id);
    }
  }, [activeTab, currentUnits]);

  useEffect(() => {
    // Perform conversion
    if (fromUnit && toUnit && fromValue) {
      const numValue = parseFloat(fromValue);
      if (!isNaN(numValue)) {
        const result = convertUnit(numValue, fromUnit, toUnit, activeTab);
        setToValue(formatNumber(result));
      }
    }
  }, [fromValue, fromUnit, toUnit, activeTab]);

  const handleSwap = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
    setFromValue(toValue);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#4a7c9b] to-[#5a8caa] px-4 py-3 flex items-center gap-3">
        <Calculator className="text-white w-6 h-6" />
        <h2 className="text-lg font-semibold text-white">{t('common.expressConverter')}</h2>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap border-b border-gray-200 bg-gray-50">
        {expressCategories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveTab(cat.id)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === cat.id
                ? 'bg-white text-[#4a7c9b] border-b-2 border-[#4a7c9b] -mb-px'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            {t(`sidebar.converters.${cat.id}`, cat.label)}
          </button>
        ))}
      </div>

      {/* Converter Form */}
      <div className="p-4">
        <div className="grid md:grid-cols-[1fr,auto,1fr] gap-4 items-end">
          {/* From Section */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">{t('common.from')}</label>
            <input
              type="number"
              value={fromValue}
              onChange={(e) => setFromValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent"
              placeholder={t('common.enterValue')}
            />
            <select
              value={fromUnit}
              onChange={(e) => setFromUnit(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent"
            >
              {currentUnits.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {t(`${getUnitTranslationKey(activeTab)}.${unit.id}.name`, unit.name)}
                </option>
              ))}
            </select>
          </div>

          {/* Swap Button */}
          <div className="flex justify-center">
            <button
              onClick={handleSwap}
              className="p-2 rounded-full bg-[#4a7c9b] text-white hover:bg-[#3d6a86] transition-colors shadow-md"
              title={t('common.swap')}
            >
              <ArrowLeftRight size={20} />
            </button>
          </div>

          {/* To Section */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">{t('common.to')}</label>
            <input
              type="text"
              value={toValue}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-700"
              placeholder={t('common.result')}
            />
            <select
              value={toUnit}
              onChange={(e) => setToUnit(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent"
            >
              {currentUnits.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {t(`${getUnitTranslationKey(activeTab)}.${unit.id}.name`, unit.name)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Result Display */}
        {fromValue && toValue && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-center text-gray-700">
              <span className="font-semibold">{fromValue}</span>{' '}
              <span className="text-gray-500">{t(`${getUnitTranslationKey(activeTab)}.${fromUnit}.name`, currentUnits.find(u => u.id === fromUnit)?.name)}</span>{' = '}
              <span className="font-semibold text-[#4a7c9b]">{toValue}</span>{' '}
              <span className="text-gray-500">{t(`${getUnitTranslationKey(activeTab)}.${toUnit}.name`, currentUnits.find(u => u.id === toUnit)?.name)}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpressConverter;
